'use client';

import {
  useMemo,
  useState,
  forwardRef,
  useCallback,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Button } from './button';
import { FilterPill, type FilterDensity } from './filter-pill';
import { Input } from './input';
import { Menu } from './menu';
import type { MenuItemProps } from './styled/menu';
import { Select } from './select';
import { BodyDefault } from './typography';
import { useTranslations } from 'next-intl';

export type FilterItem = {
  value: string;
  label: string;
  tooltip?: string;
  labelTooltip?: string;
  disabled?: boolean;
  isInitiallyExpanded?: boolean;
  children?: FilterItem[];
};
export type ResolveFilterBadgeTextParams = {
  selectedValues: string[];
  selectedItems: FilterItem[];
  items: FilterItem[];
  placeholder: string;
  disabled: boolean;
};
export type FilterProps = {
  id: string;
  items: FilterItem[];
  placeholder: string;
  value?: string[];
  onChange: (value: string[]) => void;
  onClear?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  isMultiSelect?: boolean;
  required?: boolean;
  searchable?: boolean;
  contentWidth?: string;
  menuActions?: Array<{
    label: string;
    active: boolean;
    onClick: () => void;
  }>;
  resolveBadgeText?: (params: ResolveFilterBadgeTextParams) => ReactNode;
  tooltip?: ReactNode;
  dataTestid?: string;
  density?: FilterDensity;
} & Omit<Menu.RootProps, 'children'>;
type FilterTriggerProps = ComponentProps<typeof Select.Trigger>;

export const FilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(
  (props, ref) => {
    return (
      <Select.Trigger
        ref={ref}
        h="32px"
        fontWeight="normal"
        color="fg.neutral.subtle"
        borderRadius="16px"
        _hover={{ bg: 'bg.neutral.subtleSoft', color: 'fg.neutral.muted' }}
        {...props}
      />
    );
  },
);
FilterTrigger.displayName = 'FilterTrigger';
type FilterTriggerTextProps = {
  children: ReactNode;
  color?: ComponentProps<typeof BodyDefault>['color'];
};

export const FilterTriggerText = ({
  children,
  color = 'fg.neutral.subtle',
}: FilterTriggerTextProps) => {
  return (
    <BodyDefault
      color={color}
      fontWeight="normal"
      _hover={{ color: 'fg.neutral.muted' }}
    >
      {children}
    </BodyDefault>
  );
};

FilterTriggerText.displayName = 'FilterTriggerText';

const filterItemsBySearchQuery = ({
  items,
  searchQuery,
}: {
  items: FilterItem[];
  searchQuery: string;
}): FilterItem[] => {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  if (!normalizedSearchQuery) {
    return items;
  }

  return items.flatMap((item) => {
    if (item.children?.length) {
      const matchingChildren = item.children.filter((child) =>
        child.label.toLowerCase().includes(normalizedSearchQuery),
      );

      if (matchingChildren.length > 0) {
        return [{ ...item, children: matchingChildren }];
      }

      return item.label.toLowerCase().includes(normalizedSearchQuery)
        ? [item]
        : [];
    }

    return item.label.toLowerCase().includes(normalizedSearchQuery)
      ? [item]
      : [];
  });
};

const getChildValues = (item: FilterItem): string[] =>
  item.children?.map((child) => child.value) ?? [];
const getParentSelectionState = ({
  item,
  selectedValues,
}: {
  item: FilterItem;
  selectedValues: string[];
}): boolean | 'indeterminate' => {
  const childValues = getChildValues(item);

  if (childValues.length === 0) {
    return selectedValues.includes(item.value);
  }

  const selectedChildCount = childValues.filter((childValue) =>
    selectedValues.includes(childValue),
  ).length;

  if (selectedChildCount === 0) {
    return selectedValues.includes(item.value);
  }

  if (selectedChildCount === childValues.length) {
    return true;
  }

  return 'indeterminate';
};

export const Filter = ({
  items,
  value,
  onChange,
  onClear,
  disabled = false,
  readOnly = false,
  placeholder,
  isMultiSelect = false,
  required = false,
  searchable = false,
  contentWidth,
  menuActions,
  resolveBadgeText,
  tooltip,
  dataTestid = '',
  density = 'default',
  ...menuProps
}: FilterProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const tCommon = useTranslations('designSystem.common.entityReferenceFilter');
  const selectedValues = useMemo(() => value ?? [], [value]);
  const isActive = selectedValues.length > 0;
  const isMenuDisabled = disabled || readOnly;
  const visibleItems = useMemo(() => {
    if (!searchable) {
      return items;
    }

    return filterItemsBySearchQuery({ items, searchQuery });
  }, [items, searchQuery, searchable]);
  const selectedItems = useMemo(
    () =>
      items.flatMap((item) => {
        if (item.children?.length) {
          const selectedChildren = item.children.filter((child) =>
            selectedValues.includes(child.value),
          );

          if (selectedChildren.length > 0) {
            return selectedChildren;
          }

          return selectedValues.includes(item.value) ? [item] : [];
        }

        return selectedValues.includes(item.value) ? [item] : [];
      }),
    [items, selectedValues],
  );
  const t = useTranslations('designSystem.filter');
  const selectedText = useMemo(() => {
    if (!isActive) {
      return null;
    }

    if (resolveBadgeText) {
      return resolveBadgeText({
        selectedValues,
        selectedItems,
        items,
        placeholder,
        disabled,
      });
    }

    return selectedItems.length === 1
      ? selectedItems[0].label
      : `(${selectedItems.length}) ${t('selected')}`;
  }, [
    disabled,
    isActive,
    items,
    placeholder,
    resolveBadgeText,
    selectedItems,
    selectedValues,
    t,
  ]);
  const handleItemClick = useCallback(
    (itemValue: string) => {
      const currentValues = value || [];

      if (isMultiSelect) {
        const parentItem = items.find((item) =>
          item.children?.some((child) => child.value === itemValue),
        );
        const isSelected = currentValues.includes(itemValue);
        let newValues = isSelected
          ? currentValues.filter((selectedValue) => selectedValue !== itemValue)
          : [...currentValues, itemValue];

        if (parentItem && !isSelected) {
          newValues = newValues.filter(
            (selectedValue) => selectedValue !== parentItem.value,
          );
        }

        onChange(newValues);

        return;
      }

      const isSelected = currentValues.includes(itemValue);
      const newValues =
        isSelected && !required ? [] : isSelected ? currentValues : [itemValue];

      onChange(newValues);
      setIsMenuOpen(false);
    },
    [isMultiSelect, items, onChange, required, value],
  );
  const handleParentClick = useCallback(
    (item: FilterItem) => {
      const childValues = getChildValues(item);
      const currentValues = value || [];
      const parentSelectionState = getParentSelectionState({
        item,
        selectedValues: currentValues,
      });

      if (parentSelectionState === true) {
        onChange(
          currentValues.filter(
            (selectedValue) =>
              selectedValue !== item.value &&
              !childValues.includes(selectedValue),
          ),
        );

        return;
      }

      if (parentSelectionState === 'indeterminate') {
        onChange([...new Set([...currentValues, ...childValues])]);

        return;
      }

      onChange([...new Set([...currentValues, item.value])]);
    },
    [onChange, value],
  );
  const menuItems = useMemo(
    (): MenuItemProps[] =>
      visibleItems.map((item) => {
        const hasChildren = (item.children?.length ?? 0) > 0;

        if (hasChildren) {
          return {
            value: item.value,
            label: item.label,
            tooltip: item.tooltip,
            labelTooltip: item.labelTooltip,
            disabled: item.disabled,
            isInitiallyExpanded: item.isInitiallyExpanded,
            selected: isMultiSelect
              ? getParentSelectionState({ item, selectedValues })
              : false,
            onClick: () => {
              if (isMultiSelect) {
                handleParentClick(item);
              }
            },
            nestedItems: item.children?.map((child) => ({
              value: child.value,
              label: child.label,
              tooltip: child.tooltip,
              labelTooltip: child.labelTooltip,
              disabled: child.disabled,
              selected: selectedValues.includes(child.value),
              onClick: () => handleItemClick(child.value),
            })),
          };
        }

        return {
          value: item.value,
          label: item.label,
          tooltip: item.tooltip,
          labelTooltip: item.labelTooltip,
          disabled: item.disabled,
          selected: selectedValues.includes(item.value),
          onClick: () => handleItemClick(item.value),
        };
      }),
    [
      handleItemClick,
      handleParentClick,
      isMultiSelect,
      selectedValues,
      visibleItems,
    ],
  );
  const handleClear = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled) return;
    event.stopPropagation();
    event.preventDefault();
    onChange([]);
    onClear?.();
  };

  const resolvedContentWidth =
    contentWidth ??
    (searchable || (menuActions?.length ?? 0) > 0 ? '280px' : 'max-content');

  return (
    <Menu.MenuWithItems
      {...menuProps}
      closeOnSelect={!isMultiSelect}
      open={isMenuDisabled ? false : isMenuOpen}
      onOpenChange={(details) => {
        if (!isMenuDisabled) {
          setIsMenuOpen(details.open);
          if (!details.open) {
            setSearchQuery('');
          }
        }
      }}
      positioning={{
        placement: 'bottom-start',
        strategy: 'fixed',
        ...menuProps.positioning,
      }}
      items={menuItems}
      contentWidth={resolvedContentWidth}
      header={
        searchable || menuActions?.length ? (
          <Box display="flex" flexDirection="column" gap={2} w="full">
            {searchable ? (
              <Input
                size="sm"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={tCommon('searchPlaceholder')}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              />
            ) : null}
            {menuActions?.length ? (
              <HStack gap={1}>
                {menuActions.map((action) => (
                  <Button
                    key={action.label}
                    size="xs"
                    variant={action.active ? 'solid' : 'outline'}
                    flex={1}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </HStack>
            ) : null}
          </Box>
        ) : undefined
      }
      selectionIndicator={isMultiSelect ? 'checkbox' : 'checkmark'}
      trigger={
        <FilterPill
          placeholder={placeholder}
          isActive={isActive}
          selectedText={selectedText}
          density={density}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tooltip={tooltip}
          dataTestid={dataTestid}
          onClear={handleClear}
        />
      }
    />
  );
};
